/* @flow */
import { Set, List, Record, OrderedMap, Map, fromJS } from 'immutable';
import {isImmutable} from 'trustar/utils';
import { pipe } from 'ramda';

import {
  CLEAR_LIST,
  REFETCH_REPORTS,
  FETCH_LIST,
  FETCH_LIST_SUCCESS,
  FETCH_LIST_FAILURE,
  COPY_REPORT,
  COPY_REPORT_SUCCESS,
  COPY_REPORT_FAILURE,
  MOVE_REPORT,
  MOVE_REPORT_SUCCESS,
  MOVE_REPORT_FAILURE,
  SET_CURRENT_ID,
  STORE_SUCCESS,
  SET_DISPLAY_OPTIONS,
  REMOVE_SUCCESS,
  MODIFY_DISTRIBUTION_SUCCESS,
  FETCH_METADATA,
  FETCH_METADATA_SUCCESS,
  FETCH_METADATA_FAILURE,
  SET_ATTRIBUTE_SUCCESS,
  REMOVE_ATTRIBUTE_SUCCESS,
  SET_ENCLAVE_TAG_SUCCESS,
  REMOVE_ENCLAVE_TAG_SUCCESS,
  CLEAR_REPORT_METADATA_CACHE,
} from './action-types.js';
import { REPORT_PROCESSED, UPDATE_REPORT_SUCCESS } from 'modules/create/action-types';
import {
  FETCH_SUCCESS,
  FETCH_FAILURE,
  SAVE_NOTE_SUCCESS,
  DELETE_NOTE_SUCCESS,
} from 'modules/constellation/action-types';
import { REVERT_CLASSIFICATION_SUCCESS } from 'modules/indicators/action-types';
import { PAGE_SIZE } from './constants.js';
import { CLEAR } from 'modules/constellation/action-types';
import { combineReducers } from 'redux-immutable';
import type { Action } from 'trustar/utils/redux-types.js';

import { SAVE_WHITELIST_ENTRY_SUCCESS } from 'modules/company-whitelist/action-types';
import {STORE_FROM_LOCAL} from './action-types';
import isString from 'lodash.isstring';

/* **************************************************************************
 * REPORTS
 * *************************************************************************/

export const mapMetadata = (metadata: any): Map<any, any> => {
  let data = fromJS(metadata);

  const reportEnclaveId = data.get('enclaves', List()).getIn([0, 'id'], '');
  const attributes = data.get('attributes', List())
    .toOrderedMap()
    .mapKeys((k, v) => v.get('guid'));
  const enclaveTags = data.get('tags', List())
    .map(tag => {
      if (isImmutable(tag)) {
        return tag.set('enclaveId', reportEnclaveId)
      }
      // Meaning it's a string
      return Map({name: tag, enclaveId: reportEnclaveId});
    })
    .toOrderedMap()
    .mapKeys((k, v) => v.get('name'));
  return data.withMutations(x => {
    x.set('attributes', attributes);
    x.set('tags', enclaveTags);
  });
};

type AttributeMetadata = {
  id: number,
  guid: string,
  count: number,
  name: string,
};

type EnclaveTagMetadata = {
  id: number,
  enclaveId: string,
  guid: string,
  count: number,
  name: string,
};

type EnclaveMetadata = {
  id: string,
  name: string,
};

type RawMetadata = {
  reportGuid: string,
  correlationCount: number,
  indicatorsCount: number,
  attributes: AttributeMetadata[],
  enclaveTags: EnclaveTagMetadata[],
  tags: EnclaveTagMetadata[],
  enclaves: EnclaveMetadata[],
  read: boolean,
  editable: boolean,
};

// $FlowFixMe
export const reports = (state = OrderedMap(), action: Action<*>) => {
  const { type, payload, meta } = action;
  switch (type) {
  case REMOVE_SUCCESS:
    return state.delete(payload);
  case SET_ENCLAVE_TAG_SUCCESS:
    return state.setIn([payload.id, 'data', 'tags', payload.tag.get('name')], payload.tag);
  case SET_ATTRIBUTE_SUCCESS:
    return state.setIn([payload.id, 'data', 'attributes', payload.tag.get('name')], payload.tag);
  case REMOVE_ENCLAVE_TAG_SUCCESS:
    return state.deleteIn([payload.id, 'data', 'tags',  payload.tag.get('name')]);
  case REMOVE_ATTRIBUTE_SUCCESS:
    return state.deleteIn([payload.id, 'data', 'attributes',  payload.tag.get('guid')]);
  case MODIFY_DISTRIBUTION_SUCCESS:
    return state.setIn([payload.id, 'data', 'distributionType'], payload.distributionType);
  case STORE_SUCCESS:
    const id = payload.get('id');
    return state.mergeIn([id], fromJS({
      id,
      loading: false,
      lastFetched: Date.now(),
      data: mapMetadata(payload),
    }));
  case STORE_FROM_LOCAL:
    const reportGuid = payload.get('guid');
    return state.mergeIn([reportGuid], fromJS({
      id: reportGuid,
      loading: false,
      lastFetched: Date.now(),
      data: mapMetadata(payload),
    }));
  case SAVE_WHITELIST_ENTRY_SUCCESS:
    return state.map((report) => report.setIn(['data', 'indicators'], report.getIn(['data', 'indicators']).filter(d => d.get('value') !== payload.get('value'))));
  case REVERT_CLASSIFICATION_SUCCESS:
    const indexOfIndicator = state.getIn([payload.reportId, 'data', 'indicators'], List())
    .findIndex(ioc => ioc.get('value') === payload.value);
    if (indexOfIndicator > -1) {
      return state
      .setIn([payload.reportId, 'data', 'indicators', indexOfIndicator, 'weight'], 1)
      .setIn([payload.reportId, 'data', 'indicators', indexOfIndicator, 'reason'], '');
    }
    return state;
  case UPDATE_REPORT_SUCCESS:
    return state.delete(payload);
  default:
    return state;
  }
};

const metadata = (state, action) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_METADATA:
    // Payload will be string[]. An array of ids.
    return payload.reduce(
      (state, id) => state.setIn([id, 'loading'], true),
      state
    );
  default:
    return state;
  }
};

export const reportList = (state: OrderedMap<*,*> = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case REPORT_PROCESSED:
  case CLEAR_LIST:
  case REFETCH_REPORTS: // Clear existing list whenever re-fetching
    return OrderedMap();
  case FETCH_LIST_SUCCESS:
    const fetchReports = fromJS(payload)
    .toOrderedMap()
    .mapKeys((k, v) => v.get('id'))
    .map(x => fromJS({
      id: x.get('id'),
      data: x,
      loading: false, // Start loading
    }));
    return state.concat(fetchReports);
  case FETCH_METADATA:
    return metadata(state, action);
  case FETCH_METADATA_FAILURE:
    return state.setIn([payload, 'loading'], false);
  case REMOVE_SUCCESS:
    return state.delete(payload);
  default:
    return state;
  }
};

const isFetchingList = (state = true, action) => {
  switch (action.type) {
  case FETCH_LIST:
    return true;
  case FETCH_LIST_SUCCESS:
  case FETCH_LIST_FAILURE:
    return false;
  default:
    return state;
  }
};

const reportActionAsyncInProgress = (state = false, action) => {
  switch (action.type) {
    case COPY_REPORT:
    case MOVE_REPORT:
      return true;
  case COPY_REPORT_SUCCESS:
  case COPY_REPORT_FAILURE:
  case MOVE_REPORT_SUCCESS:
  case MOVE_REPORT_FAILURE:
    return false;
  default:
    return state;
  }
};

/* **************************************************************************
 * Current Report
 * *************************************************************************/

// $FlowFixMe
export const currentReportId = (state = null, action: Action<*>) => {
  const { payload, type } = action;
  switch(type) {
  case CLEAR:
    return null;
  case SET_CURRENT_ID:
    return payload;
  case FETCH_SUCCESS:
  case FETCH_FAILURE:
    return payload.id;
  case REMOVE_SUCCESS:
    return null;
  default:
    return state;
  }
};

/* **************************************************************************
 * Reports Display Options
 * *************************************************************************/

const DefaultOptions = Record({
  page: 0,
  hasNext: false,
  search: '',
  tags: List(),
  excludedTags: List(),
  enclaveFilters: Set(),
  includeCommunity: true,
});

const fromOptions = pipe(
  fromJS,
  x => x.update('enclaveFilters', x => x ? Set(x) : x)
);

// $FlowFixMe
export const options = (state = DefaultOptions(), action: Action<*>) => {
  const { type, payload } = action;
  switch(type) {
  case SET_DISPLAY_OPTIONS:
    return state.merge(fromOptions(payload));
  case FETCH_LIST_SUCCESS:
    return state.set('hasNext', (payload.size < PAGE_SIZE ? false : true));
  default:
    return state;
  }
};

const increment = x => x + 1;
const decrement = x => x - 1;

/**
 * Fetching metadata is meant to be fire and forget. This means we want to avoid
 * numerous unecessary calls to fetch metadata. Thus the cache was born!
 */
export const metadataCache = (state: Map<string, *> = Map(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case UPDATE_REPORT_SUCCESS:
    return state.delete(payload.id);
  case SET_ENCLAVE_TAG_SUCCESS:
    if (payload.id && state.has(payload.id)) {
      const newTag = payload.tag;
      return state.update(payload.id, report => report.setIn(['tags', newTag.get('name')], newTag));
    }

    return state;
  case STORE_SUCCESS:
    const id = payload.get('id');
    return state.has(id) ? state.update(id, s => s.set('read', true)) : state;
  case FETCH_METADATA_SUCCESS: {
    const updated = fromJS(payload)
      .toMap()
      .mapKeys((k, v) => v.get('reportGuid'))
      .map(mapMetadata)
      .map(x => x.set('updatedAt', Date.now()));
    return state.merge(updated);
  }
  case CLEAR_REPORT_METADATA_CACHE:
    return Map();
  case DELETE_NOTE_SUCCESS:
  case SAVE_NOTE_SUCCESS: {
    const id = payload.nodeGuid;
    const updater = type === DELETE_NOTE_SUCCESS ? decrement : increment;

    // Manually update note count when user adds a new note in app
    // Only update state if necessary to avoid adding a key with an undefined
    // value
    if (state.get(id)) {
      return state.update(id, x => x.update('noteCount', updater));
    } else {
      return state;
    }
  }
  default:
    return state;
  }
};

export default combineReducers({
  reports,
  reportList,
  isFetchingList,
  currentReportId,
  options,
  metadataCache,
  reportActionAsyncInProgress,
});



// WEBPACK FOOTER //
// ./src/app/modules/reports/stores.js